/**
 * CSV import wizard — `/catalog/imports/new` + `/catalog/imports/:importId`
 * (SPEC screen 7). Stepper: Upload → Review validation → Commit → Result.
 * Upload is multipart (`file` + optional `allow_explicit_sku_renames`) and is
 * accepted with 202 — rows validate asynchronously, so the wizard polls
 * through `uploaded`/`validating` and shows an interstitial until the report
 * lands. `failed` is a terminal error state surfacing `failure_code`. The
 * current step derives from the import record's status, so a deep-linked or
 * reloaded page resumes at the right step. Commit sends an Idempotency-Key
 * held for the session; `import_already_committed` jumps to the result step.
 */
import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import { Download, FileUp } from "lucide-react";
import {
  Badge,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  cn,
  toast,
} from "@erp/ui";

import { DataTable, badgeCell } from "@/components/data-table";
import type { BadgeCellVariant } from "@/components/data-table";
import { ApiError } from "@/lib/api/errors";
import { isIdempotencyKeyReuse, useIdempotentMutation } from "@/features/shared/ops-api";
import { formatDateTime, formatNumber } from "@/lib/format";

import {
  IMPORT_ROW_STATUSES,
  commitProductImport,
  downloadProductImportFailures,
  uploadProductImport,
  type ImportRowStatus,
  type ProductImportRow,
} from "../api";
import { catalogKeys, useImportDetail, useImportRows, type ImportRowFilters } from "../queries";

const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
const ALL = "__all__";

const ROW_STATUS_VARIANTS: Record<string, BadgeCellVariant> = {
  pending: "outline",
  valid: "success",
  error: "destructive",
};

type WizardStep = 1 | 2 | 3 | 4;

function saveBrowserDownload(blob: Blob, filename: string): void {
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = filename;
  anchor.hidden = true;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(href), 0);
}

export interface ImportWizardPageProps {
  /** Undefined on `/catalog/imports/new` (upload step). */
  importId?: string;
  /** Upload created the import record — navigate to its detail route. */
  onUploaded: (importId: string) => void;
  onBackToList: () => void;
  /** Page-size override for the rows table (tests use a small limit). */
  rowsLimit?: number;
  canCommit?: boolean;
}

export function ImportWizardPage({
  importId,
  onUploaded,
  onBackToList,
  rowsLimit,
  canCommit = true,
}: ImportWizardPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const [uploadError, setUploadError] = useState<string | null>(null);
  const [allowRenames, setAllowRenames] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ImportRowStatus | typeof ALL>(ALL);
  const [skuSearch, setSkuSearch] = useState("");

  const importQuery = useImportDetail(importId);
  const record = importQuery.data;

  const rowFilters = useMemo<ImportRowFilters>(() => {
    const built: ImportRowFilters = {};
    if (statusFilter !== ALL) built.status = statusFilter;
    if (skuSearch !== "") built.sku = skuSearch;
    return built;
  }, [statusFilter, skuSearch]);

  // Rows exist only once async validation has finished staging them.
  const rowsReady =
    record !== undefined &&
    (record.status === "validated" ||
      record.status === "committing" ||
      record.status === "committed");
  const rowsQuery = useImportRows(rowsReady ? importId : undefined, rowFilters, rowsLimit);

  const validating =
    record !== undefined && (record.status === "uploaded" || record.status === "validating");

  const step: WizardStep =
    importId === undefined || record === undefined
      ? 1
      : record.status === "committed" || record.status === "failed"
        ? 4
        : record.status === "committing"
          ? 3
          : 2;

  // Idempotency-Key held for the session: retry/double-click replays safely.
  const uploadMutation = useIdempotentMutation({
    mutationFn: (input: { file: File; allowRenames: boolean }, key: string) =>
      uploadProductImport(input.file, input.allowRenames, key),
    onSuccess: (created) => {
      setUploadError(null);
      void queryClient.invalidateQueries({ queryKey: catalogKeys.imports.all });
      onUploaded(created.id);
    },
    onError: (error) => {
      const problem = error instanceof ApiError ? error.problem : undefined;
      setUploadError(
        problem?.detail ??
          problem?.title ??
          t("catalog.imports.uploadFailed", "Upload failed — the file was not staged."),
      );
    },
  });

  const commitMutation = useIdempotentMutation({
    mutationFn: (_: void, key: string) => commitProductImport(importId as string, key),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: catalogKeys.imports.all });
    },
    onError: (error) => {
      if (isIdempotencyKeyReuse(error)) {
        toast({
          variant: "destructive",
          title: t(
            "catalog.imports.keyReuse",
            "This import was already committed with different options — refresh.",
          ),
        });
        return;
      }
      if (error instanceof ApiError && error.code === "import_already_committed") {
        // Someone else finished it — jump to the result step via fresh state.
        void queryClient.invalidateQueries({ queryKey: catalogKeys.imports.all });
        return;
      }
      toast({
        variant: "destructive",
        title: t("catalog.imports.commitFailed", "Commit failed — retrying is safe."),
      });
    },
  });

  const failuresDownload = useMutation({
    mutationFn: downloadProductImportFailures,
    onSuccess: ({ blob, filename }) => saveBrowserDownload(blob, filename),
    onError: () => {
      toast({
        variant: "destructive",
        title: t("catalog.imports.downloadFailed", "Failure rows could not be downloaded."),
      });
    },
  });

  const pickFile = (file: File | undefined) => {
    if (file === undefined) return;
    // Client pre-checks only size/extension; parsing + validation is server-side.
    if (!file.name.toLowerCase().endsWith(".csv")) {
      setUploadError(t("catalog.imports.notCsv", "Only .csv files can be staged."));
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadError(t("catalog.imports.tooLarge", "The file is larger than 5 MB."));
      return;
    }
    // The File goes on the wire as multipart form data. The idempotency layer
    // reads only the already-enforced ≤5 MiB bytes to bind the retry key to
    // exact content; CSV parsing and validation remain server-side.
    uploadMutation.mutate({ file, allowRenames });
  };

  const rowStatusLabel = (value: string): string => t(`catalog.imports.rowStatus.${value}`, value);

  const columns = useMemo<ColumnDef<ProductImportRow>[]>(
    () => [
      {
        accessorKey: "row_no",
        id: "row_no",
        header: t("catalog.imports.columns.rowNo", "Row"),
        enableSorting: false,
        meta: { numeric: true },
        cell: (ctx) => String(ctx.getValue<number>()),
      },
      {
        accessorKey: "sku",
        id: "sku",
        header: t("catalog.imports.columns.sku", "SKU"),
        enableSorting: false,
        cell: (ctx) => (
          <span dir="ltr" className="font-mono">
            {ctx.getValue<string>()}
          </span>
        ),
      },
      {
        accessorKey: "status",
        id: "status",
        header: t("catalog.imports.columns.rowStatus", "Status"),
        enableSorting: false,
        cell: (ctx) => {
          const value = ctx.getValue<string>();
          return badgeCell(value, ROW_STATUS_VARIANTS, rowStatusLabel(value));
        },
      },
      {
        accessorKey: "errors",
        id: "errors",
        header: t("catalog.imports.columns.rowErrors", "Errors"),
        enableSorting: false,
        cell: (ctx) => {
          const errors = ctx.getValue<ProductImportRow["errors"]>();
          if (errors.length === 0) {
            return <span className="text-muted-foreground">&mdash;</span>;
          }
          // Codes + messages render verbatim — new server codes need no UI change.
          return (
            <span className="flex flex-wrap gap-1">
              {errors.map((error, index) => (
                <Badge key={`${error.code}-${index}`} variant="destructive" className="gap-1">
                  <span dir="ltr" className="font-mono">
                    {error.code}
                  </span>
                  <span>{error.message}</span>
                </Badge>
              ))}
            </span>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- label helper derives from t
    [t],
  );

  const stepLabel = (index: WizardStep, label: string) => (
    <li
      key={index}
      aria-current={step === index ? "step" : undefined}
      className={cn(
        "flex items-center gap-2 text-sm",
        step === index ? "font-semibold text-foreground" : "text-muted-foreground",
      )}
    >
      <span
        className={cn(
          "flex size-6 items-center justify-center rounded-full border text-xs",
          step >= index && "border-primary bg-primary text-primary-foreground",
        )}
      >
        {formatNumber(index, i18n.language)}
      </span>
      {label}
    </li>
  );

  const progressFraction =
    record !== undefined && record.clean_count > 0
      ? record.committed_count / record.clean_count
      : 0;

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">
          {t("catalog.imports.wizardTitle", "Import products from CSV")}
        </h1>
        <Button variant="outline" onClick={onBackToList}>
          {t("catalog.imports.backToList", "All imports")}
        </Button>
      </div>

      {/* Stepper flows from the inline-start edge; RTL mirrors via flex. */}
      <ol className="flex flex-wrap items-center gap-4">
        {stepLabel(1, t("catalog.imports.step1", "Upload"))}
        {stepLabel(2, t("catalog.imports.step2", "Review validation"))}
        {stepLabel(3, t("catalog.imports.step3", "Commit"))}
        {stepLabel(4, t("catalog.imports.step4", "Result"))}
      </ol>

      {step === 1 ? (
        <div
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            pickFile(event.dataTransfer.files[0]);
          }}
          className="flex flex-col items-center gap-3 rounded-md border border-dashed p-10 text-center"
        >
          <FileUp className="size-8 text-muted-foreground" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">
            {t("catalog.imports.dropHint", "Drop a CSV here, or choose a file.")}
          </p>
          <Label htmlFor="import-file" className="sr-only">
            {t("catalog.imports.chooseFile", "Choose CSV file")}
          </Label>
          <Input
            id="import-file"
            type="file"
            accept=".csv,text/csv"
            className="max-w-xs"
            disabled={uploadMutation.isPending}
            onChange={(event) => pickFile(event.target.files?.[0])}
          />
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            {/* Native checkbox keeps the entire bilingual label as its click target. */}
            <input
              type="checkbox"
              className="size-4 accent-primary"
              checked={allowRenames}
              onChange={(event) => setAllowRenames(event.target.checked)}
            />
            {t("catalog.imports.allowRenames", "Allow explicit SKU renames")}
          </label>
          {uploadError !== null ? (
            <p role="alert" className="text-sm text-destructive">
              {uploadError}
            </p>
          ) : null}
        </div>
      ) : null}

      {importId !== undefined && importQuery.isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : null}

      {importId !== undefined && importQuery.isError ? (
        <div role="alert" className="flex flex-col items-start gap-3 p-4">
          <p className="text-sm text-muted-foreground">
            {t("catalog.imports.detailLoadError", "The import could not be loaded.")}
          </p>
          <Button variant="outline" onClick={() => void importQuery.refetch()}>
            {t("actions.retry")}
          </Button>
        </div>
      ) : null}

      {record !== undefined && record.status === "discarded" ? (
        <p role="status" className="text-sm text-muted-foreground">
          {t("catalog.imports.discarded", "This import was discarded — upload a new file.")}
        </p>
      ) : null}

      {validating ? (
        <div role="status" className="flex flex-col items-start gap-2 rounded-md border p-6">
          <p className="text-sm font-medium">
            {t("catalog.imports.validating", "Validating rows…")}
          </p>
          <p className="text-sm text-muted-foreground">
            {t(
              "catalog.imports.validatingHint",
              "The report appears when validation finishes — safe to leave this page.",
            )}
          </p>
        </div>
      ) : null}

      {record !== undefined && !validating && (step === 2 || step === 3) ? (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <span dir="ltr" className="font-mono">
              {record.filename}
            </span>
            <span>
              {t("catalog.imports.rowCounts", "{{total}} rows · {{clean}} clean · {{bad}} errors", {
                total: formatNumber(record.total_rows, i18n.language),
                clean: formatNumber(record.clean_count, i18n.language),
                bad: formatNumber(record.error_count, i18n.language),
              })}
            </span>
            <span className="text-muted-foreground">
              {t("catalog.imports.stagingExpires", "Staging expires {{date}}", {
                date: formatDateTime(record.staging_expires_at, i18n.language),
              })}
            </span>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="import-rows-status">
                {t("catalog.imports.filters.status", "Status")}
              </Label>
              <Select
                value={statusFilter}
                onValueChange={(value) => setStatusFilter(value as ImportRowStatus | typeof ALL)}
              >
                <SelectTrigger id="import-rows-status" className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>{t("catalog.imports.filters.all", "All")}</SelectItem>
                  {IMPORT_ROW_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>
                      {rowStatusLabel(status)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="import-rows-sku">
                {t("catalog.imports.filters.sku", "Search SKU")}
              </Label>
              <Input
                id="import-rows-sku"
                dir="ltr"
                className="w-48"
                value={skuSearch}
                onChange={(event) => setSkuSearch(event.target.value)}
              />
            </div>
            <div className="ms-auto flex items-center gap-2">
              {record.error_csv_available ? (
                <Button
                  type="button"
                  variant="outline"
                  loading={failuresDownload.isPending}
                  onClick={() => failuresDownload.mutate(record.id)}
                >
                  <Download className="size-4" aria-hidden="true" />
                  {t("catalog.imports.downloadFailures", "Download {{count}} failures", {
                    count: record.error_count,
                  })}
                </Button>
              ) : null}
              {canCommit ? (
                <Button
                  type="button"
                  disabled={record.clean_count === 0 || commitMutation.isPending || step === 3}
                  loading={commitMutation.isPending}
                  onClick={() => commitMutation.mutate()}
                >
                  {t("catalog.imports.commit", "Commit {{count}} clean rows", {
                    count: record.clean_count,
                  })}
                </Button>
              ) : null}
            </div>
          </div>

          {step === 3 ? (
            <div role="status" className="flex flex-col gap-2 rounded-md border p-3">
              <p className="text-sm">
                {t(
                  "catalog.imports.committing",
                  "Committed {{done}} of {{total}} — safe to leave this page; the job continues server-side.",
                  {
                    done: formatNumber(record.committed_count, i18n.language),
                    total: formatNumber(record.clean_count, i18n.language),
                  },
                )}
              </p>
              <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full bg-primary"
                  // Counts are ints — the ratio is safe in a JS number.
                  style={{ inlineSize: `${Math.min(100, Math.round(progressFraction * 100))}%` }}
                />
              </div>
            </div>
          ) : null}

          <DataTable
            columns={columns}
            query={rowsQuery}
            getRowId={(row) => row.id}
            showColumnsMenu={false}
            emptyMessage={t("catalog.imports.rowsEmpty", "No rows match this filter.")}
            errorMessage={t("catalog.imports.rowsLoadError", "Rows could not be loaded.")}
          />
        </div>
      ) : null}

      {record !== undefined && step === 4 && record.status === "failed" ? (
        <div
          role="alert"
          className="flex flex-col items-start gap-3 rounded-md border border-destructive/50 bg-destructive/10 p-6"
        >
          <p className="text-base font-medium text-destructive">
            {t("catalog.imports.failedTitle", "Import failed")}
          </p>
          <p className="text-sm text-destructive">
            {record.failure_code !== undefined
              ? t("catalog.imports.failedWithCode", "This file could not be staged ({{code}}).", {
                  code: record.failure_code,
                })
              : t("catalog.imports.failedDetail", "This file could not be staged.")}
          </p>
          <p className="text-sm text-muted-foreground">
            {t("catalog.imports.failedHint", "Fix the file and upload it as a new import.")}
          </p>
        </div>
      ) : null}

      {record !== undefined && step === 4 && record.status === "committed" ? (
        <div className="flex flex-col items-start gap-3 rounded-md border p-6">
          <p className="text-base font-medium" role="status">
            {t("catalog.imports.resultLine", "{{done}} created or updated · {{bad}} failed", {
              done: formatNumber(record.committed_count, i18n.language),
              bad: formatNumber(record.error_count, i18n.language),
            })}
          </p>
          {record.error_csv_available ? (
            <Button
              type="button"
              variant="outline"
              loading={failuresDownload.isPending}
              onClick={() => failuresDownload.mutate(record.id)}
            >
              <Download className="size-4" aria-hidden="true" />
              {t("catalog.imports.downloadFailuresResult", "Download failure rows as CSV")}
            </Button>
          ) : null}
          <p className="text-sm text-muted-foreground">
            {t(
              "catalog.imports.reuploadHint",
              "Fix the failure file and upload it as a new import.",
            )}
          </p>
        </div>
      ) : null}
    </div>
  );
}
